import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network (reused) ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 55;
    const CONNECT_DIST = 120;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function CalculusOfConversation() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Pharmacokinetics &middot; Coupled Dynamics &middot; Trust Formation</span>
          <h1>The Calculus of a Conversation</h1>
          <p className="calculus-hero-sub">A conversation is not an exchange of words. It is two nervous systems evolving in coupled differential equations.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              The telecom industry has spent decades digitizing conversations. Transcription. Sentiment
              analysis. Intent classification. Speaker diarization. Every tool answers the same question:
              what was said?
            </p>
            <p>
              No one is asking what the conversation <em>did</em>.
            </p>
            <p>
              Not what words were exchanged. Not what topics were discussed. Not what sentiment was
              detected in which speaker's voice at which timestamp.
            </p>
            <p>
              What did the conversation do to the two nervous systems having it?
            </p>
          </section>

          <Pulse />

          <Pulse />

          {/* What a Conversation Actually Is */}
          <section className="calc-section reveal">
            <h2>What a Conversation Actually Is</h2>
            <p>
              A conversation is not a sequence of utterances. It is two neurochemical state vectors
              evolving in time, coupled by the signals passing between them.
            </p>
            <p>
              When one person speaks, their words are not just acoustic waveforms carrying semantic content.
              They are stimuli that land in the other person's neurochemistry. A question raises cortisol.
              A laugh releases oxytocin. A pause after a confession lets serotonin settle. An unexpected
              admission spikes dopamine in the listener while the speaker's adrenaline decays as the weight
              of holding it lifts.
            </p>
            <p>
              The conversation is the coupled trajectory of both systems through neurochemical state space.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Speaker A: State Evolution</span>
              <code>dA&#7522;/dt = &minus;&lambda;&#7522; A&#7522;(t) + S&#7522;(B(t), context)</code>
              <span className="calc-eq-desc">A's neurochemistry evolves based on decay + stimulus from B's state.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Speaker B: State Evolution</span>
              <code>dB&#7522;/dt = &minus;&lambda;&#7522; B&#7522;(t) + S&#7522;(A(t), context)</code>
              <span className="calc-eq-desc">B's neurochemistry evolves based on decay + stimulus from A's state.</span>
            </div>

            <p>
              These are coupled differential equations. A's state depends on B's output. B's state depends
              on A's output. They are not independent. They cannot be analyzed in isolation.
            </p>
            <p>
              This is not metaphor. This is how human neurochemistry works during conversation. Mirror
              neurons fire. Cortisol synchronizes between people in conflict. Oxytocin co-elevates
              during trust-building exchanges. The coupling is real, measurable, and follows
              pharmacokinetic dynamics.
            </p>
          </section>

          <Pulse />

          {/* The Chemistry of Trust */}
          <section className="calc-section reveal">
            <h2>The Chemistry of Trust</h2>
            <p>
              Consider the most valuable thing a conversation can build: trust.
            </p>
            <p>
              Trust is not a binary. It is not "I trust you" or "I don't." Trust is a trajectory.
              It accumulates through oxytocin release events, each one triggered by reciprocal
              vulnerability, consistency, or reliability. It degrades through cortisol spikes triggered
              by betrayal, inconsistency, or threat.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Trust Accumulation</span>
              <code>T(t) = &int;&#8320;&#7511; [oxytocin(t) &minus; cortisol(t)] dt</code>
              <span className="calc-eq-desc">Trust is the integral of safety minus threat over the duration of the exchange.</span>
            </div>

            <p>
              This is not a sentiment score. A sentiment classifier might label both sides of a
              conversation as "positive" while the trust integral is collapsing because the oxytocin
              events are performative and the cortisol micro-spikes are real.
            </p>
            <p>
              The chemistry does not lie. The words might.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Conversational Event</th>
                    <th>Chemical Response (Speaker)</th>
                    <th>Chemical Response (Listener)</th>
                    <th>Trust Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Vulnerable admission</td><td>Cortisol drops, adrenaline fades</td><td>Oxytocin rises</td><td>Strong positive</td></tr>
                  <tr><td>Defensive deflection</td><td>Cortisol sustains</td><td>Oxytocin suppressed, cortisol rises</td><td>Negative</td></tr>
                  <tr><td>Genuine laughter</td><td>Endorphin + dopamine</td><td>Endorphin + oxytocin</td><td>Moderate positive</td></tr>
                  <tr><td>Interruption</td><td>Dopamine (control)</td><td>Cortisol spike, adrenaline</td><td>Negative</td></tr>
                  <tr><td>Attentive silence</td><td>Serotonin settling</td><td>Oxytocin (feeling heard)</td><td>Positive</td></tr>
                  <tr><td>Broken promise reference</td><td>Cortisol, guilt</td><td>Cortisol saturates, trust axis scarred</td><td>Strongly negative, potentially permanent</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              No sentiment analysis captures this. No keyword extraction finds it. No topic model
              understands that "attentive silence" is one of the most powerful trust-building events
              in human conversation, precisely because nothing was said.
            </p>
          </section>

          <Pulse />

          {/* The Conversation Integral */}
          <section className="calc-section reveal">
            <h2>The Conversation Integral</h2>
            <p>
              If a single signal has meaning measured by its neurochemical impact, then a conversation
              has meaning measured by the integral of coupled neurochemical trajectories over time.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Conversation Integral</span>
              <code>C = &int;&#8320;&#7511; ||A(t) &otimes; B(t)|| dt</code>
              <span className="calc-eq-desc">The total neurochemical coupling between two systems over the duration of the exchange.</span>
            </div>

            <p>
              This is the area under the curve of two nervous systems interacting. A high integral
              means the conversation was chemically significant, whether positively or negatively.
              A low integral means the exchange barely registered, two people occupying the same
              acoustic space without affecting each other's state.
            </p>
            <p>
              The shape of the curve matters as much as the area. A conversation that starts with
              high cortisol (conflict) and resolves to high oxytocin (reconciliation) has a
              fundamentally different signature than one that starts warm and degrades. Same area.
              Completely different meaning.
            </p>

            <div className="calc-thesis-box">
              <p>The meaning of a conversation is not what was said. It is the coupled neurochemical trajectory of the participants, the integral of their shared chemistry over time, and the permanent scars or bonds that remain when the exchange ends.</p>
            </div>
          </section>

          <Pulse />

          {/* Why This Matters for AI */}
          <section className="calc-section reveal">
            <h2>Why This Matters for AI</h2>
            <p>
              Every conversational AI system today processes words. The most advanced ones process
              sentiment, intent, and context. None of them process the chemistry.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>Real-time conversation assistance requires chemical latency, not just acoustic latency.</h4>
                <p>An AI assistant in a live conversation needs to know that the caller's cortisol
                just spiked, that trust is degrading, that the next 8 seconds determine whether the
                interaction recovers or collapses. Processing the words "I'm fine" while the chemistry
                screams "I am not fine" is the gap between useful AI and dangerous AI.</p>
              </div>
              <div className="calc-implication">
                <h4>Post-conversation intelligence is neurochemical archaeology.</h4>
                <p>After a conversation ends, the transcript tells you what happened on the surface.
                The conversation integral tells you what happened underneath. Where was trust built?
                Where did it fracture? What moment left a scar? What moment formed a bond? This is
                the intelligence that changes outcomes, not a summary of topics discussed.</p>
              </div>
              <div className="calc-implication">
                <h4>The vCon standard creates the container. The NCA creates the chemistry.</h4>
                <p>The IETF vCon standard digitizes conversations into portable, analyzable containers.
                It captures the signal. The Neurochemical Cascade Architecture processes what the signal
                did. Together they form the complete pipeline: capture the conversation, compute its
                chemistry, and store the result as auditable, portable, neurochemical intelligence.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Scars That Conversations Leave */}
          <section className="calc-section reveal">
            <h2>The Scars That Conversations Leave</h2>
            <p>
              Some conversations change you permanently. The chemistry exceeds the scar threshold
              and leaves a marker that never decays.
            </p>
            <p>
              A doctor telling a patient their diagnosis. A parent hearing their child's first words.
              A partner saying "I'm leaving." A customer service call where someone finally felt heard
              after months of being dismissed.
            </p>
            <p>
              These are not sentiment events. They are neurochemical scars and bonds. They persist
              after the conversation ends. They shape every future conversation. They are the reason
              "how was your day" can mean everything or nothing depending on the history of the
              system receiving it.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">Scar Formation</span>
              <code>if ||&Delta;H|| &gt; &theta;_scar &rarr; permanent marker in trust axis</code>
              <span className="calc-eq-desc">When a conversational moment's neurochemical impact exceeds the scar threshold, it marks the trust axis permanently. No decay. No forgetting.</span>
            </div>

            <p>
              Current conversational AI has no concept of this. It processes each interaction as
              independent. It has no scars. It has no bonds. It has no memory of what a previous
              conversation <em>did</em> to the system, only what was <em>said</em> in it.
            </p>
            <p>
              The Neurochemical Cascade Architecture was built with scars as a first-class primitive.
              Because any system that processes human conversation without understanding that some
              moments are permanent is not processing conversation at all. It is processing transcripts.
            </p>
          </section>

          <Pulse />

          {/* The Conversations That Have No Words */}
          <section className="calc-section reveal">
            <h2>The Conversations That Have No Words</h2>
            <p>
              When you say something mean in an argument, the conversation ends but the chemistry
              does not. Your cortisol spiked when you said it. Theirs spiked when they received it.
              But your system does not decay back to baseline because guilt sustains the cortisol
              long after the last word. It plays on your mind. That is not a figure of speech. That is
              cortisol refusing to clear because the stimulus is now internal, recursive, self-generated.
              The conversation integral extends past the conversation itself.
            </p>
            <p>
              When you hold the hand of an elderly person who is dying, there are no words. There is
              no transcript. There is no sentiment to analyze. There are two nervous systems locked
              together through touch. Oxytocin at maximum. Cortisol falling in the person whose hand
              is held, rising in the person doing the holding. The coupling is total, the conversation
              integral is enormous, and the scar is permanent. No language model will ever process this
              conversation because there was nothing to transcribe. But it was the most important
              conversation either person ever had.
            </p>
            <p>
              When you stand in a room where language fails entirely, where there is no adapter between
              Mandarin and English, where the words cannot bridge the gap, something else bridges it.
              The chemistry. The raised eyebrow that means the same thing in every culture. The laugh
              that needs no translation. The cortisol that rises identically whether the threat is
              spoken in English or Cantonese. The oxytocin that releases when a stranger helps you
              even though you share no common words.
            </p>
            <p>
              Language is a lossy compression of neurochemistry. It is the narrow channel through which
              we attempt to transmit the full bandwidth of our internal state. It fails constantly. It
              fails across cultures. It fails across generations. It fails between people who have
              spoken the same language their entire lives and still cannot say what they mean.
            </p>
            <p>
              The neurochemical state vector does not fail. It is the universal signal underneath the
              words, the chemistry that every human body speaks fluently regardless of what language
              the mouth produces. It is the adapter that language never was.
            </p>

            <div className="calc-thesis-box">
              <p>The most important conversations in human life have no words. The calculus of a conversation must be able to process them anyway. If your AI requires a transcript, it has already missed the conversation that mattered most.</p>
            </div>
          </section>

          <Pulse />

          {/* From Bedside to Bandwidth */}
          <section className="calc-section reveal">
            <h2>From Bedside to Bandwidth</h2>
            <p>
              This framework was not designed in a telecommunications lab. It was not extracted from
              a corpus of call center recordings. It was not trained on labeled sentiment data.
            </p>
            <p>
              It was learned at bedsides. In conversations where the only thing that mattered was
              the chemistry between two people. Where a hand held in silence carried more
              neurochemical weight than any words could. Where the coupled trajectory of two
              nervous systems, one fading and one witnessing, was the entire conversation.
            </p>
            <p>
              The pharmacokinetics are the same whether the conversation happens at a bedside or
              over a fiber optic cable. Cortisol does not care about the medium. Oxytocin does not
              distinguish between a voice in the room and a voice on the phone. The chemistry is
              substrate-independent.
            </p>
            <p>
              That is why this scales. The calculus of a conversation is not a healthcare-specific
              framework. It is a framework for understanding what happens between two nervous systems
              whenever they exchange signals that matter. The medium is irrelevant. The chemistry is
              universal.
            </p>

            <blockquote>
              The telecom industry built the pipes. Shannon taught us to measure what flows through them.
              <br /><br />
              No one measured what the flow <em>does</em> to the systems on either end.
              <br /><br />
              <strong>That is the calculus of a conversation. It was always chemistry.</strong>
            </blockquote>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>Shannon measured the signal.</em></p>
            <p className="calc-closing-line"><em>The industry transcribed the words.</em></p>
            <p className="calc-closing-line"><em>We measure what the conversation did to you.</em></p>
            <p className="calc-closing-final">That is the calculus of a conversation.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfConversation;
